import Link from 'next/link';
import React, { useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import verifyEmailIcon from '@/public/images/icons/verify-email.svg';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';
import doNewVerificationEmailWithAuth from '@/api/doNewVerificationEmailWithAuth';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { BaseApiResponse } from '@/types/api/api-v2';
import { useGoogleCaptcha } from '@/context/GoogleCaptchaProvider';
import ReCaptchaDisclaimer from './common/RecaptchaDisclaimer';

const EmailVerificationModal = ({
  show,
  onHide,
}: {
  show: boolean;
  onHide: any;
}) => {
  const { executeRecaptcha, isScriptLoaded } = useGoogleCaptcha();

  const { t: tCommon } = useTranslation('common');
  const [confirmation, setConfirmation] = useState(false);
  const handleResend = async () => {
    if (isScriptLoaded) {
      const toastId = toast.loading(tCommon('sending_email_request'));
      try {
        const captcha = await executeRecaptcha('resend_email_verification');
        await doNewVerificationEmailWithAuth(captcha);
        toast.dismiss(toastId);
        toast.success(tCommon('email_sent_successfully'));
      } catch (err: unknown) {
        if (err instanceof AxiosError) {
          toast.dismiss(toastId);
          const axiosErrorData = err.response
            ?.data as BaseApiResponse<null> | null;
          if (axiosErrorData) {
            const axiosErrorMessage = axiosErrorData?.meta?.message;
            if (axiosErrorMessage === 'User not found') {
              return toast.success(
                tCommon('a_user_with_this_email_was_not_found')
              );
            } else if (axiosErrorMessage === 'Email already verified') {
              return toast.success(tCommon('email_is_already_verified'));
            } else if (axiosErrorMessage === 'Invalid Captcha') {
              return toast.success(
                tCommon('invalid_captcha_try_again_or_refresh')
              );
            } else if (axiosErrorMessage === 'Captcha is required') {
              return toast.error(tCommon('captcha_is_required'));
            }
          }
        }
        return toast.error(tCommon('an_unexpected_error_occurred'));
      }
    } else {
      toast.error(tCommon('captcha_is_required'));
    }
  };
  return (
    <>
      <Modal show={show} onHide={() => onHide()}>
        <Modal.Header closeButton>
          <Modal.Title className="text-center font-size-16 fw-bold w-100">
            {translationFallback(
              'complete_your_account',
              tCommon('complete_your_account'),
              'Complete your account'
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h2 className="font-size-18 fw-bold w-100 mb-3">
            {translationFallback(
              'complete_your_account',
              tCommon('complete_your_account'),
              'Complete your account'
            )}
          </h2>
          <p className="font-size-16 mb-6">
            {translationFallback(
              'we_sent_email',
              tCommon('we_sent_email'),
              'We just sent an email! Click on the link in the email and confirm your email address'
            )}
          </p>
          <Row>
            <Col className="text-center">
              <Image
                src={verifyEmailIcon}
                alt="Verify Email"
                width={143}
                height={143}
                className="mb-6"
              />
            </Col>
          </Row>
          <Row>
            <Col>
              {confirmation && (
                <p className="text-success font-size-16">
                  {translationFallback(
                    'a_new_verification_link',
                    tCommon('a_new_verification_link'),
                    'A new verification link has been sent to the email address you provided during registration'
                  )}
                </p>
              )}
            </Col>
          </Row>
          <Row>
            <Col className="d-grid">
              <Link href="/" className="mb-3 py-3 btn btn-s-primary" type="">
                {translationFallback(
                  'explore_spiritory',
                  tCommon('explore_spiritory'),
                  'Explore Spiritory'
                )}
              </Link>
            </Col>
          </Row>
          <Row>
            <Col className="d-grid">
              <Button variant="link" onClick={handleResend} className="py-3">
                {translationFallback(
                  'resend_confirmation_email',
                  tCommon('resend_confirmation_email'),
                  'Resend confirmation email'
                )}
              </Button>
              <ReCaptchaDisclaimer />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EmailVerificationModal;
